import React from 'react';
import { Formik } from 'formik';
import { orderValidationSchema, ORDER_EDIT_REQUIRED_FIELDS } from './schema';

function orderFieldHighlight(fieldName) {
  const required = ORDER_EDIT_REQUIRED_FIELDS.includes(fieldName);
  return {
    required,
    groupClass: required ? 'modal-field-group modal-field-group--required' : 'modal-field-group',
    labelClass: required ? 'modal-field-label modal-field-label--required' : 'modal-field-label',
    inputClass: required ? 'modal-input modal-input--required' : 'modal-input',
    asterisk: required ? (
      <span className="modal-required-asterisk" aria-hidden="true">
        *
      </span>
    ) : null,
  };
}

const initialTouched = ORDER_EDIT_REQUIRED_FIELDS.reduce(
  (acc, key) => ({ ...acc, [key]: true }),
  {},
);

function OrderEditModal({ order, onClose, onSave, updateLoading }) {
  if (!order) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 className="modal-title">Edit Order Details</h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
          >
            <span style={{ fontSize: '1.5rem', color: '#555' }}>&times;</span>
          </button>
        </div>
        <p className="modal-subtitle">Order Number: {order.orderNumber}</p>
        <p className="modal-required-hint" role="note">
          Highlighted fields are required to save.
        </p>

        <Formik
          initialValues={order}
          initialTouched={initialTouched}
          validationSchema={orderValidationSchema}
          validateOnMount={true}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={async (values) => {
            await onSave(values);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldTouched,
            isSubmitting,
          }) => {
            const rq = {
              shipToAddress1: orderFieldHighlight('shipToAddress1'),
              shipToCity: orderFieldHighlight('shipToCity'),
              shipToState: orderFieldHighlight('shipToState'),
              shipToPostalCode: orderFieldHighlight('shipToPostalCode'),
              shipToCountry: orderFieldHighlight('shipToCountry'),
              billToAddress1: orderFieldHighlight('billToAddress1'),
              billToCity: orderFieldHighlight('billToCity'),
              billToState: orderFieldHighlight('billToState'),
              billToPostalCode: orderFieldHighlight('billToPostalCode'),
              billToCountry: orderFieldHighlight('billToCountry'),
            };
            return (
            <form onSubmit={handleSubmit}>
              <div className="modal-form-grid">
                {/* Email Address - readonly */}
                <div className="modal-field-group">
                  <label className="modal-field-label">Email Address</label>
                  <input
                    type="text"
                    name="emailAddress"
                    value={values.billToEmail}
                    readOnly
                    className="modal-input modal-input-readonly"
                  />
                </div>

                {/* Ship To First Name - readonly */}
                <div className="modal-field-group">
                  <label className="modal-field-label">Ship To First Name</label>
                  <input
                    type="text"
                    name="shipToFirstName"
                    value={values.shipToFirstName}
                    readOnly
                    className="modal-input modal-input-readonly"
                  />
                </div>

                {/* Ship To Last Name - readonly */}
                <div className="modal-field-group">
                  <label className="modal-field-label">Ship To Last Name</label>
                  <input
                    type="text"
                    name="shipToLastName"
                    value={values.shipToLastName}
                    readOnly
                    className="modal-input modal-input-readonly"
                  />
                </div>

                {/* Ship To Address 1 - editable */}
                <div className={rq.shipToAddress1.groupClass}>
                  <label className={rq.shipToAddress1.labelClass}>
                    Ship To Address 1
                    {rq.shipToAddress1.asterisk}
                  </label>
                  <input
                    type="text"
                    name="shipToAddress1"
                    value={values.shipToAddress1}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldTouched('shipToAddress1', true, false);
                    }}
                    onBlur={handleBlur}
                    className={rq.shipToAddress1.inputClass}
                    aria-required={rq.shipToAddress1.required}
                  />
                  {touched.shipToAddress1 && errors.shipToAddress1 && (
                    <p className="modal-validation-error">{errors.shipToAddress1}</p>
                  )}
                </div>

                {/* Ship To Address 2 - editable */}
                <div className="modal-field-group">
                  <label className="modal-field-label">Ship To Address 2</label>
                  <input
                    type="text"
                    name="shipToAddress2"
                    value={values.shipToAddress2}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="modal-input"
                  />
                </div>

                {/* Ship To City - editable */}
                <div className={rq.shipToCity.groupClass}>
                  <label className={rq.shipToCity.labelClass}>
                    Ship To City
                    {rq.shipToCity.asterisk}
                  </label>
                  <input
                    type="text"
                    name="shipToCity"
                    value={values.shipToCity}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldTouched('shipToCity', true, false);
                    }}
                    onBlur={handleBlur}
                    className={rq.shipToCity.inputClass}
                    aria-required={rq.shipToCity.required}
                  />
                  {touched.shipToCity && errors.shipToCity && (
                    <p className="modal-validation-error">{errors.shipToCity}</p>
                  )}
                </div>

                {/* Ship To State - editable */}
                <div className={rq.shipToState.groupClass}>
                  <label className={rq.shipToState.labelClass}>
                    Ship To State
                    {rq.shipToState.asterisk}
                  </label>
                  <input
                    type="text"
                    name="shipToState"
                    value={values.shipToState}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldTouched('shipToState', true, false);
                    }}
                    onBlur={handleBlur}
                    className={rq.shipToState.inputClass}
                    aria-required={rq.shipToState.required}
                  />
                  {touched.shipToState && errors.shipToState && (
                    <p className="modal-validation-error">{errors.shipToState}</p>
                  )}
                </div>

                {/* Ship To Zip - editable */}
                <div className={rq.shipToPostalCode.groupClass}>
                  <label className={rq.shipToPostalCode.labelClass}>
                    Ship To Zip
                    {rq.shipToPostalCode.asterisk}
                  </label>
                  <input
                    type="text"
                    name="shipToPostalCode"
                    value={values.shipToPostalCode}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldTouched('shipToPostalCode', true, false);
                    }}
                    onBlur={handleBlur}
                    className={rq.shipToPostalCode.inputClass}
                    aria-required={rq.shipToPostalCode.required}
                  />
                  {touched.shipToPostalCode && errors.shipToPostalCode && (
                    <p className="modal-validation-error">{errors.shipToPostalCode}</p>
                  )}
                </div>

                {/* Ship To Country - editable */}
                <div className={rq.shipToCountry.groupClass}>
                  <label className={rq.shipToCountry.labelClass}>
                    Ship To Country
                    {rq.shipToCountry.asterisk}
                  </label>
                  <input
                    type="text"
                    name="shipToCountry"
                    value={values.shipToCountry}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldTouched('shipToCountry', true, false);
                    }}
                    onBlur={handleBlur}
                    className={rq.shipToCountry.inputClass}
                    aria-required={rq.shipToCountry.required}
                  />
                  {touched.shipToCountry && errors.shipToCountry && (
                    <p className="modal-validation-error">{errors.shipToCountry}</p>
                  )}
                </div>

               
                  {/* Ship To Phone - editable */}
                  <div className="modal-field-group">
                    <label className="modal-field-label">Ship To Phone</label>
                    <input
                      type="text"
                      name="shipToPhoneNumber"
                      value={values.shipToPhoneNumber ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className="modal-input "
                    />
                  </div>

                {/* Bill To Address 1 - editable */}
                <div className={rq.billToAddress1.groupClass}>
                  <label className={rq.billToAddress1.labelClass}>
                    Bill To Address 1
                    {rq.billToAddress1.asterisk}
                  </label>
                  <input
                    type="text"
                    name="billToAddress1"
                    value={values.billToAddress1}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldTouched('billToAddress1', true, false);
                    }}
                    onBlur={handleBlur}
                    className={rq.billToAddress1.inputClass}
                    aria-required={rq.billToAddress1.required}
                  />
                  {touched.billToAddress1 && errors.billToAddress1 && (
                    <p className="modal-validation-error">{errors.billToAddress1}</p>
                  )}
                </div>

                {/* Bill To Address 2 - editable */}
                <div className="modal-field-group">
                  <label className="modal-field-label">Bill To Address 2</label>
                  <input
                    type="text"
                    name="billToAddress2"
                    value={values.billToAddress2}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="modal-input"
                  />
                </div>

                {/* Bill To City - editable */}
                <div className={rq.billToCity.groupClass}>
                  <label className={rq.billToCity.labelClass}>
                    Bill To City
                    {rq.billToCity.asterisk}
                  </label>
                  <input
                    type="text"
                    name="billToCity"
                    value={values.billToCity}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldTouched('billToCity', true, false);
                    }}
                    onBlur={handleBlur}
                    className={rq.billToCity.inputClass}
                    aria-required={rq.billToCity.required}
                  />
                  {touched.billToCity && errors.billToCity && (
                    <p className="modal-validation-error">{errors.billToCity}</p>
                  )}
                </div>

                {/* Bill To State - editable */}
                <div className={rq.billToState.groupClass}>
                  <label className={rq.billToState.labelClass}>
                    Bill To State
                    {rq.billToState.asterisk}
                  </label>
                  <input
                    type="text"
                    name="billToState"
                    value={values.billToState}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldTouched('billToState', true, false);
                    }}
                    onBlur={handleBlur}
                    className={rq.billToState.inputClass}
                    aria-required={rq.billToState.required}
                  />
                  {touched.billToState && errors.billToState && (
                    <p className="modal-validation-error">{errors.billToState}</p>
                  )}
                </div>

                {/* Bill To Zip - editable */}
                <div className={rq.billToPostalCode.groupClass}>
                  <label className={rq.billToPostalCode.labelClass}>
                    Bill To Zip
                    {rq.billToPostalCode.asterisk}
                  </label>
                  <input
                    type="text"
                    name="billToPostalCode"
                    value={values.billToPostalCode}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldTouched('billToPostalCode', true, false);
                    }}
                    onBlur={handleBlur}
                    className={rq.billToPostalCode.inputClass}
                    aria-required={rq.billToPostalCode.required}
                  />
                  {touched.billToPostalCode && errors.billToPostalCode && (
                    <p className="modal-validation-error">{errors.billToPostalCode}</p>
                  )}
                </div>

                {/* Bill To Country - editable */}
                <div className={rq.billToCountry.groupClass}>
                  <label className={rq.billToCountry.labelClass}>
                    Bill To Country
                    {rq.billToCountry.asterisk}
                  </label>
                  <input
                    type="text"
                    name="billToCountry"
                    value={values.billToCountry}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldTouched('billToCountry', true, false);
                    }}
                    onBlur={handleBlur}
                    className={rq.billToCountry.inputClass}
                    aria-required={rq.billToCountry.required}
                  />
                  {touched.billToCountry && errors.billToCountry && (
                    <p className="modal-validation-error">{errors.billToCountry}</p>
                  )}
                </div>

                {/* Bill To Phone - editable */}
                <div className="modal-field-group">
                  <label className="modal-field-label">Bill To Phone</label>
                  <input
                    type="text"
                    name="billToPhoneNumber"
                    value={values.billToPhoneNumber ?? ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="modal-input "
                  />
                </div>
              </div>

              <div className="modal-buttons">
                <button type="button" onClick={onClose} className="modal-close-button">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-cancel-button"
                  disabled={isSubmitting || updateLoading}
                >
                  Save Changes
                </button>
              </div>
            </form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
}

export default OrderEditModal